import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { RootState } from "../../store.interface";
import { WizardState } from "@/shared/interfaces/WizardState.interface";

import { ApiErrorWrapper } from "@/shared/interfaces/ApiErrorWrapper";
import { Step, StepsGroup } from "@/shared/interfaces/Step.interface";
import { StepDone } from "@/shared/interfaces/StepDone.interface";
import { getErrorObjectByPath } from "@/shared/scripts/ValuesByPath";
import { StepsConfig } from "@/encoding/scripts/steps";
import { i18n } from "@/shared/scripts/helpers/translation/i18n";
import { router} from "@/shared/router/index";

import progress from "./progress";
import form from "../encoding/form";

const modules = {
    progress: progress,
    form: form
};

const state: WizardState = {
    config: {} as StepsConfig,
    steps: [] as Array<Step>,
    groups: [] as Array<StepsGroup>,
    current: {} as Step,
    validated: [],
    done: [],
    indexVisited: 0,
    errors: null,
};

const mutations: MutationTree<WizardState> = {
    config: function(state, config: StepsConfig) {
        state.config = config;
    },

    steps: function(state, steps: Step[]) {
        state.steps = steps;
    },

    remove: function(state, stepName: string) {
        const index = state.steps.findIndex(step => step.step == stepName);
        state.steps.splice(index, 1);
    },

    current: function(state, step: Step) {
        if (state.current) {
            state.current.done = true;
            state.current.current = false;
        }
        step.current = true;
        state.current = step;
    },

    validated: function(state, stepName: string) {
        state.validated.push(stepName);
    },

    initValidated: function(state, steps: Array<string>) {
        state.validated = steps;
    },

    done: function(state, steps: Array<StepDone>) {
        state.done = steps;
    },

    addToDone: function(state, step: Step) {
        state.done.push({ "step": step.step, "index": step.index as number });
    },

    errors: function(state, errors: ApiErrorWrapper) {
        state.errors = errors;
    },

    groups: function(state, groups: StepsGroup[]) {
        state.groups = groups;
    },
    setIndexVisited: function(state, index: number) {
        state.indexVisited = index;
    },
};

const actions: ActionTree<WizardState, RootState> = {

    /**
     * Initialize the state:
     * - get a StepsConfig object
     * - add properties to the steps
     * - group steps (top-level steps with their substeps)
     * - set current step and current group
     */
    // init({ state, getters, commit, dispatch }, {config, whereItComes}) {
    init: function({ state, commit, dispatch }, { config, isEditionMode = false }) {
        commit("config", config);
        const lastStepIndex = config.steps.length - 1;

        /**
         * Add properties to steps. In order to do that, the steps array of the
         * initial config is cloned, leaving the initial object untouched.
         */
        const steps = [...config.steps].map((step, index) => ({
            ...step,
            current: false,
            validated: isEditionMode,
            done: isEditionMode,
            isFirst: index === 0,
            isLast: index === lastStepIndex,
            index: index,
            error: null,
        }));
        commit("setIndexVisited", isEditionMode ? lastStepIndex : 0);
        commit("steps", steps);
        commit("current", steps[0]);
        if(state.validated.length > 0) {
            dispatch("initValidatedSteps", state.validated);
        }
        dispatch("validate", steps[0].step);

        dispatch("createGroups");
        dispatch("currentGroup", state.groups[0]);
    },

    /**
     * Create groups of steps. The ones without sub steps are Step.
     */
    createGroups: function({ state, getters, commit }) {
        commit("progress/mainMax", getters.groupsNames.length);

        const groups: StepsGroup[] = getters.groupsNames.map((groupName: string) => {
            const group = {
                error: null,
                isGroup: false,
                enabled: false,
                // add group name (`step`) and label if found among “real” steps
                ...state.steps.find(step => step.step === groupName),
            };

            // Label found: the group is a step and doesn’t have sub steps.
            if (group.step) {
                return group;
            }

            // The group is a parent and should contain sub steps.
            return {
                isGroup: true,
                validated: false,
                current: false,
                steps: state.steps.filter(step => step.parent === groupName),
                error: null,

                // name (currently named `step`) and label found among “parent” steps
                ...state?.config?.groups?.find(groupStep => groupStep.step === groupName),
            };
        });

        commit("groups", groups);
    },

    currentGroup: function({ state, commit, dispatch }, group: StepsGroup) {
        dispatch("resetGroups");
        dispatch("checkValidatedGroups");
        group.current = true;
        group.enabled = true;

        const index = state.groups.findIndex(groupStep => groupStep.step == group.step);

        // @todo: Check if the following lines could be moved to the progress module.
        commit("progress/hasSub", group.isGroup);
        commit("progress/main", index + 1);
        if (group.isGroup) {
            commit("progress/sub", group.steps.findIndex(step => step.current) + 1);
            commit("progress/subMax", group.steps.length);
        }
    },

    resetGroups: function({ state }) {
        state.groups.forEach(groupStep => {
            groupStep.current = false;
        });
    },

    checkValidatedGroups: function({state}) {
        state.groups.forEach(group => {
            if(group.isGroup) {
                group.enabled = group.steps?.some(step => step.done) ?? false;
            }
        });
    },

    reset: function({ commit }, steps: Array<string>) {
        commit("initValidated", steps);
        commit("done", []);
        // dispatch("init", state.config);
    },

    remove: function({ commit, dispatch, state }, stepNames: string[]) {
        /**
         * TODO: Think how to avoid to reset everything and recreate groups twice.
         *
         * Mehdi’s note: I think this action is properly done. However, when
         * changing a property or transaction type, it triggers the `init`
         * action, which then triggers `createGroups`. With a dedicated
         * new action for these two, we should be able to solve this
         * double call to `createGroups` (= we copy-paste `init`,
         * but without the call to `createGroups`?).
         */
        stepNames.forEach(step => commit("remove", step));
        dispatch("createGroups");
        dispatch("currentGroup", state.groups[0]);
    },

    validate: function({ commit, state }, stepName: string) {
        if (state.validated.indexOf(stepName) === -1) {
            commit("validated", stepName);
        }
    },

    initValidatedSteps: function({ commit, dispatch, state }, steps:Array<string>) {
        commit("initValidated", steps);
        state.steps
            .filter(step => steps.includes(step.step))
            .forEach(step => step.done = true);
        dispatch("checkValidatedGroups");
    },

    setPreviousStepToDone: function({state}, stepName: string) {
        let stop = false;

        // Iterate until the current step (then `stop` becomes true).
        state.groups.forEach(group => {
            if(group.steps) {
                group.steps.forEach(step => {
                    if(step.step !== stepName && !stop) {
                        step.done = true;
                    }
                    if(step.step === stepName && !stop) {
                        step.done = true;
                        stop = true;
                    }
                });
            } else {
                if(group.step !== stepName && !stop) {
                    group.done = true;
                    group.validated = true;
                }
                if(group.step === stepName && !stop) {
                    group.done = true;
                    group.validated = true;
                    stop = true;
                }
            }
        });
    },

    moveBy: function({ state,dispatch, commit, rootGetters }, delta) {
        const targetIndex = state.steps.findIndex(step => step.current) + delta;

        if (targetIndex < 0 || targetIndex >= state.steps.length) { return; }

        commit("current", state.steps[targetIndex]);
        dispatch("validate", state.current.step);

        /**
         * Hacky way to change translated URL as long as routeToPage() is
         * tied to the main Vue instance. We could instead compute URL
         * translations in a function and import it in modules that
         * need it.
         */
        let urlTranslated = i18n.t(state.current.step) as string;
        const classifiedId = router.currentRoute.params.id || rootGetters["FormModule/getClassifiedId"];
        if(classifiedId) {
            urlTranslated = urlTranslated + classifiedId;
        }

        router.push(urlTranslated);
    },

    previous: function({ dispatch }) {
        dispatch("moveBy", -1);
    },

    next: function({ dispatch }) {
        dispatch("moveBy", 1);
    },

    current: function({ commit, dispatch, state }, step: Step) {

        // update current step
        commit("current", step);
        if(step.index && state.indexVisited < step.index) {
            commit("setIndexVisited", step.index);
        }

        // update current group
        const currentGroup = state.groups.find(group => {
            return group.step === step.parent || group.step === step.step;
        });
        dispatch("currentGroup", currentGroup);
    },
    setErrors: function({ state }, stepErrorName: string) {
        state.groups.forEach(group => {
            if(group.steps) {
                group.steps.forEach(step => {
                    if(step.step === stepErrorName) {
                        step.error = true;
                        group.error = true;
                    }
                });
            } else {
                if(group.step === stepErrorName) {
                    group.error = true;
                }
            }
        });
    },
    resetErrors: function({state}) {
        state.groups.forEach(group => {
            group.steps?.forEach(step => step.error = false);
            group.error = false;
        });
    },
    setGroupsUnreachable: function({state}) {
        const groupsToBeUnreachable: Array<StepsGroup> = state.groups.filter(group => ["step-pricing", "step-payment"].includes(group.step));
        groupsToBeUnreachable.forEach(group => {
            group.done = false;
            group.validated = false;
            group.enabled = false;
        });
    },
};

const getters: GetterTree<WizardState, RootState> = {
    getErrorObjectByPath: state => (path: string): string | null => {
        return getErrorObjectByPath(state.errors, path);
    },

    isDone: state => (stepName: string): boolean => {
        return state.done.some(step => step.step === stepName);
    },

    getStep: state => (step: string): Step => {
        const object = state.steps.filter(item => {
            return item.step === step;
        })[0];

        return object;
    },

    getSteps: (state: WizardState): Step[] => {
        return state.steps;
    },

    getCurrent: (state: WizardState): Step => {
        return state.current;
    },

    getValidatedSteps: (state: WizardState): Array<string> => {
        return state.validated;
    },

    groupsNames: (state): string[] => {

        /**
         * Gather groups names:
         * - when a step has no children, the group name is the step label.
         * - when a step is a children, its group name is the parent label.
         */
        const groups = state.steps.map(step => step.parent ?? step.step);

        return [...new Set(groups)]; // remove duplicated groups.
    },

    currentGroup: (state): StepsGroup => {
        return state.groups.find(group => group.current) as StepsGroup;
    },
    hasErrorInCurrentGroup: (state) => (group: StepsGroup): boolean => {
        let hasError = false;
        if(group.steps) {
            group.steps.forEach((step: Step) => {
                if(step.error && step.index && state.indexVisited > step.index){
                    hasError = true;
                }
            });
        } else {
            if(group.error && group.index && state.indexVisited > group.index){
                hasError = true;
            }
        }
        return hasError;
    },
    shouldDisplayError: function (state): boolean {
        return state.indexVisited === state.current.index;
    },
    getIndexVisited: function (state): number {
        return state.indexVisited;
    }
};

const stepsWizard: Module<WizardState, RootState> = {
    namespaced: true,
    modules: modules,
    state: state,
    mutations: mutations,
    actions: actions,
    getters: getters,
};

export default stepsWizard;
